<template>
  <div class="filter-button-days-open-wrapper">
    <b-dropdown
      id="filter-button-days-open-dropdown"
      class="search-types-dropdown days-open-dropdown"
      :ref="section.filter"
      no-caret
      :disabled="disableFilters"
      boundary="viewport"
    >
      <template #button-content>
        <span class="d-sm-inline">
          {{ buttonLabel }}
        </span>
        <font-awesome-icon class="right-icon days-open-dropdown--chevron" :icon="['far', 'chevron-down']"/>
      </template>
      <div v-if="section.display_type === 'radio'" class="filter-dropdown">
        <b-dropdown-text class="filter-label">
          <h3 class="m-0">{{ section.label }}</h3>
          <button class="dropdown-close" @click="closeDropdown()">
            <font-awesome-icon :icon="['far','times']" />
          </button>
        </b-dropdown-text>
        <div class="dd-items">
          <b-dropdown-item-button
            class="filter-option"
            v-for="(option, optionIndex) in section.options"
            :class="isOptionSelected(option) ? 'selected' : ''"
            :key="optionIndex"
            @click="selectTag(option)">
            <font-awesome-icon
              class="radio-button-icon"
              :icon="isOptionSelected(option) ? radioIcons.selected : radioIcons.unSelected"/>
            {{ option.label }}
          </b-dropdown-item-button>
          <b-button
            class="custom-dates-button filter-option"
            :class="dateRangeClass"
            @click="activateCalendar()">
            <font-awesome-icon
              class="radio-button-icon"
              :icon="selectedDates.start && selectedDates.end ? radioIcons.selected : radioIcons.unSelected"/>
            Date range
          </b-button>
        </div>
        <div fluid class="p-0 m-auto calendar-container" v-if="activeCalendar">
          <div
            class="calendar-wrapper d-flex flex-column flex-sm-row">
            <vc-date-picker
              v-model="selectedDates"
              :value="null"
              color="yellow"
              is-range
              is-expanded
              :columns="$screens({ default: 1, md: 2 })"
              :rows="$screens({ default: 2, md: 1 })"
              :min-date='new Date()'
            />
          </div>
        </div>
      </div>
      <div v-if="section.display_type === 'multi_button'" class="filter-range">
        <b-dropdown-text class="filter-label">
          {{ section.label }}
        </b-dropdown-text>
        <b-dropdown-text class="filter-range-options">
          <b-button
              class="filter-range-option-button"
              v-for="(option, optionIndex) in section.options"
              :key="optionIndex"
              :pressed="filterSelected(section.filter, option.value)"
              @click="selectTag(option)"
              variant="outline-secondary"
              size="sm"
          >
            {{ option.label }}
          </b-button>
        </b-dropdown-text>
      </div>
    </b-dropdown>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { find, includes } from 'lodash';

export default {
  name: 'FilterDaysOpenButton',

  props: {
    section: Object,
    disableFilters: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    selectedDates: {
      start: null,
      end: null,
    },
    radioIcons: {
      unSelected: ['fal', 'circle'],
      selected: ['fas', 'circle'],
    },
    optionValue: null,
    activeCalendar: false,
  }),

  async created() {
    const preSelectedFromDate = this.sectionFilterSelected('date_open_from');
    const preSelectedToDate = this.sectionFilterSelected('date_open_to');

    if (preSelectedFromDate.length) {
      this.selectedDates.start = new Date(preSelectedFromDate.replace(/-/g, '/'));
    }

    if (preSelectedToDate.length) {
      this.selectedDates.end = new Date(preSelectedToDate.replace(/-/g, '/'));
    }

    if (preSelectedFromDate.length || preSelectedToDate.length) {
      this.activateCalendar()
    }
  },

  mounted() {
    this.$root.$on('bv::dropdown::show', (bvEvent) => {
      if (bvEvent.componentId === 'filter-button-days-open-dropdown') {
        this.$nextTick(() => {
          const dropdown = this.$refs[this.section.filter]
          if (dropdown && dropdown.$refs.menu) {
            dropdown.updatePopper()
          }
        })
      }
    })

    this.$root.$on('bv::dropdown::hide', (bvEvent) => {
      if (bvEvent.componentId === 'filter-button-days-open-dropdown'
        && this.activeCalendar
        && (!this.fromDateValue || !this.toDateValue)
      ) {
        this.activeCalendar = false
      }
    })
  },

  computed: {
    ...mapGetters('filter', [
      'filterSelected',
      'sectionFilterSelected',
    ]),

    buttonLabel() {
      return this.sectionFilterSelectedLabel || this.optionValue || 'All Dates';
    },
    fromDateValue() {
      return this.selectedDates.start;
    },
    toDateValue() {
      return this.selectedDates.end;
    },
    sectionFilterSelectedValue() {
      return this.sectionFilterSelected('days_open')
    },
    sectionFilterSelectedLabel() {
      if (this.sectionFilterSelected('date_open_from')?.length && this.sectionFilterSelected('date_open_to')?.length) {
        return 'Date range'
      }

      const raw = this.sectionFilterSelected('days_open');

      if (!raw || (Array.isArray(raw) && !raw.length)) {
        return '';
      }

      return find(this.section.options, ['value', raw])?.label || '';
    },
    dateRangeClass() {
      return this.sectionFilterSelected('date_open_from')?.length && this.sectionFilterSelected('date_open_to')?.length ? 'active' : '';
    },
  },

  watch: {
    selectedDates(date) {
      if (date.start || date.end) {
        this.optionValue = 'Date range';
        this.selectRange({
          date_open_from: date?.start || null,
          date_open_to: date?.end || null,
        });
      }
    },
    sectionFilterSelectedValue(value) {
      if (value?.length) {
        this.optionValue = this.sectionFilterSelectedLabel;

        if (this.filterSelected('days_open', value) && this.activeCalendar) {
          this.activateCalendar()
        }

        this.activeCalendar = false
        this.selectedDates = {
          start: null,
          end: null,
        }
      } else {
        this.optionValue = null
      }
    },
  },

  methods: {
    ...mapActions('filter', [
      'addFilter',
      'removeFilter',
    ]),

    isOptionSelected(option) {
      const hasNoFilter = !this.sectionFilterSelectedValue?.length
      const isAllDatesOption = option.value === 'all-dates' || option.value === 'all'
      const hasDateRange = this.sectionFilterSelected('date_open_from')?.length && this.sectionFilterSelected('date_open_to')?.length
      if (hasNoFilter && isAllDatesOption && !hasDateRange) return true
      return this.filterSelected(this.section.filter, option.value)
    },

    async activateCalendar() {
      this.activeCalendar = !this.activeCalendar
      if (this.selectedDate?.start && this.selectedDate?.end) {
        if (!this.activeCalendar) {
          this.selectedDates = {
            fromDate: null,
            toDate: null,
          }
          this.closeDropdown()
          this.optionValue = null
        } else {
          this.optionValue = 'Date range'
        }
      }
    },

    async selectTag(option) {
      this.optionValue = option.label;
      let localOptionValue = option.value;

      if (includes(['checkbox', 'buttons'], this.section.display_type)) {
        localOptionValue = [option.value]
      }
      await this.addFilter({ filter: this.section.filter, type: this.section.display_type, options: localOptionValue })
      this.activeCalendar = false
      this.selectedDates = {
        start: null,
        end: null,
      }
    },

    closeDropdown() {
      this.$refs[this.section.filter].$el.getElementsByClassName('dropdown-toggle')[0].click();
    },

    async selectRange(dates) {
      const tzoffset = (new Date()).getTimezoneOffset() * 60000;

      const dateOpenFrom = new Date(dates.date_open_from - tzoffset)
        .toISOString().slice(0, 10);

      const dateOpenTo = new Date(dates.date_open_to - tzoffset)
        .toISOString().slice(0, 10);

      await this.addFilter({
        filter: this.section.filter,
        type: 'date_range',
        options: {
          date_open_from: dateOpenFrom,
          date_open_to: dateOpenTo,
        },
      });

      this.closeDropdown()
    },
  },
}
</script>

<style lang="scss">
.filter-button-days-open-wrapper {
  .days-open-dropdown {
    position: initial;
    margin-right: 0.5rem;

    > button {
      background-color: $color-yellow-lighter;
      border-radius: 4px;
      border: 1px solid rgba(28, 36, 51, 0.20);
    }

    &.show {
      > .btn-secondary.dropdown-toggle {
        color: inherit !important;
        background-color: $color-yellow-light;
        border: 1px solid rgba(28, 36, 51, 0.20);
      }
    }

    > ul {
      top: 26px !important;
    }

    &--chevron {
      font-size: 12px;
    }
  }

  .dropdown-menu {
    .b-dropdown-text {
      font-size: 20px;
      font-family: 'NGS 2017';
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .filter-dropdown,
    .filter-range-options * {
      font-size: 14px !important;
    }
  }

  .filter-option {
    &.selected {
      .dropdown-item {
        font-weight: 600 !important;
      }
    }

    .dropdown-item {
      padding: 12px 24px;
      display: flex;
      justify-content: flex-start;
      align-items: center;
    }
  }

  @media (max-width: 1200px) {
    .btn-group,
    .btn-group-vertical,
    .dropdown {
      position: initial;
    }

    .dropdown-menu {
      top: -40px !important;
    }
  }

  .filter-dropdown {
    min-width: 16rem;

    .custom-dates-button {
      text-align: left;
      padding: 12px 24px;
      border-radius: 0;
      font-size: 14px;
      border-bottom: solid 1px rgba(28, 36, 51, 0.2);

      svg {
        &.radio-button-icon {
          margin-right: 5px;
        }
      }

      @include media-breakpoint-down(lg) {
        padding-left: 0.45rem;

        &.active {
          font-weight: 600;
        }

        &:not(:disabled):not(.disabled):active {
          background-color: $primary !important;
          border-color: $primary !important;

          &:hover {
            box-shadow: none;
          }
        }
      }
    }
  }

  .filter-range {
    min-width: 19rem;
    .filter-range-option-button {
      &:not(:last-child) {
        margin-right: 0.539rem;
      }
    }
  }
}

.vc-container {
  border: none;

  .vc-day {
    .vc-highlights {
      &.vc-day-layer {
        .vc-day-layer {
          &.vc-day-box-right-center {
            .vc-highlight.vc-highlight-base-start {
              background-color: $color-yellow-lighter !important;
            }
          }
          &.vc-day-box-center-center {
            .vc-highlight {
              background-color: $primary !important;
            }

            .vc-highlight.vc-highlight-base-middle {
              background-color: $color-yellow-lighter !important;
            }
          }
        }
      }
    }
  }

  .vc-day-content {
    &.vc-focusable {
      &:not(.is-disabled) {
        color: rgb(58, 58, 58) !important;
      }
    }
  }
}

@include media(tablet) {
  .calendar-wrapper {
    border-radius: 0.25rem;
    background-color: #ffffff;
    border: 1px solid $color-yellow-light;
    .date-range-calendar {
      &:not(:first-child) {
        border-left: 1px solid $color-yellow-light;
        @include media(mobile) {
          border-left: none;
          border-top: 1px solid $color-yellow-light;
        }
      }
    }
  }
}
</style>
